(() => {
  'use strict';

  const body = document.body;
  const engine = window.TitanMotion;
  if (!body.classList.contains('template-index') || !engine || window.TWLuxuryHome?.version) return;

  const main = document.getElementById('MainContent');
  const reducedQuery = matchMedia('(prefers-reduced-motion: reduce)');
  const compactQuery = matchMedia('(max-width: 749px)');
  const tabletQuery = matchMedia('(min-width: 750px) and (max-width: 1180px)');
  const factories = new Map();
  const instances = new Map();
  const tracked = new Set();
  const metrics = new WeakMap();
  const progressRecords = new WeakMap();
  const valueRecords = new Map();
  const styleCache = new WeakMap();
  const themeOverrides = new Map();
  const surfaceLayers = new Map();
  const surfaceNames = ['obsidian', 'paper', 'product', 'archive', 'art', 'close'];
  // Glaettung ist zeitbasiert (damping). "velocity" ist nur noch eine Notbremse
  // gegen Sprünge, kein Tempolimit mehr: zu niedrige Werte machten es physikalisch
  // unmoeglich, den Zielzustand zu erreichen (dossier .38 => min. 2.6s fuer 0->1),
  // wodurch Uebergangszustaende als Dauerzustand stehenblieben.
  // damping = Reaktionshaerte (hoch = schnell/hart), velocity = Sprungbremse.
  // Weiches Gleiten braucht NIEDRIGES damping bei HOHER velocity: die Bewegung
  // laeuft sanft nach, erreicht ihr Ziel aber immer. Beides hoch war der Grund
  // fuer den ruckartigen, schlagenden Eindruck.
  const channelConfig = {
    direct: { damping: 20, velocity: 12 },
    section: { damping: 7, velocity: 9 },
    text: { damping: 6.5, velocity: 9 },
    signature: { damping: 5, velocity: 8 },
    dossier: { damping: 4.2, velocity: 7 },
    material: { damping: 6.5, velocity: 9 }
  };

  // Strikte Reihenfolge je Uebergang, gemessen in Viewporthoehen VOR der Sectionkante:
  //   1. Austritt des alten Inhalts   (exitFrom -> exitTo)
  //   2. kurze Materialuebergabe      (zone, endet bei zoneSettle)
  //   3. Eintritt des neuen Inhalts   (enterFrom -> enterTo)
  // Vorher liefen alle drei ueberlappend: der Grund wechselte, waehrend alter UND
  // neuer Text sichtbar waren -> matschige Zwischentoene und Kanten quer durch Text.
  // Der Eintritt muss laufen, sobald die Section real im Bild ist - sonst steht
  // sichtbare, aber leere Flaeche. Die Materialuebergabe bleibt kurz und liegt
  // dazwischen. Werte sind Viewporthoehen vor der Sectionkante.
  // enterTo MUSS unter settle liegen: sonst erscheint der Inhalt der neuen
  // Section, bevor der Grund gewechselt hat - z.B. weisse Schwellenschrift auf
  // noch hellem Papier. Der Eintritt endet jetzt knapp NACH der Materialuebergabe.
  // enterFrom MUSS unter settle liegen, nicht nur enterTo: sonst BEGINNT der
  // Inhalt einzublenden, waehrend der Grund noch der alte ist.
  const seq = () => (compactQuery.matches
    ? { exitFrom: .52, exitTo: .14, settle: .26, enterFrom: .24, enterTo: -.10 }
    : { exitFrom: .58, exitTo: .16, settle: .28, enterFrom: .26, enterTo: -.12 });
  // Sichtbare Materialzone: kurz. Mobil ~14svh, Desktop ~20svh.
  const zoneFor = (material) => {
    const compact = compactQuery.matches;
    if (material) return compact ? .16 : .20;
    return compact ? .10 : .14;
  };
  const zoneSettle = () => seq().settle;

  let sections = [];
  let measurePending = true;
  let frameContext = null;
  let lastTime = 0;
  let settling = false;
  let destroyed = false;
  let resizeObserver = null;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (start, end, value) => {
    const x = clamp((value - start) / Math.max(.0001, end - start));
    return x * x * (3 - 2 * x);
  };

  const setStyle = (node, property, value) => {
    if (!node) return;
    let cache = styleCache.get(node);
    if (!cache) {
      cache = new Map();
      styleCache.set(node, cache);
    }
    if (cache.get(property) === value) return;
    cache.set(property, value);
    node.style.setProperty(property, value);
  };

  const setDataset = (node, key, value) => {
    if (node?.dataset[key] !== value) node.dataset[key] = value;
  };

  const measure = () => {
    const scrollY = window.scrollY || 0;
    [...tracked].forEach((node) => {
      if (!node?.isConnected) tracked.delete(node);
    });
    const nodes = new Set([...tracked, ...sections]);
    nodes.forEach((node) => {
      if (!node?.isConnected) return;
      const rect = node.getBoundingClientRect();
      metrics.set(node, {
        top: rect.top + scrollY,
        height: Math.max(1, rect.height),
        bottom: rect.bottom + scrollY
      });
    });
    instances.forEach((instance) => instance.measure?.({ metrics, viewportHeight: innerHeight, viewportWidth: innerWidth }));
    measurePending = false;
  };

  const recordFor = (node, key) => {
    let records = progressRecords.get(node);
    if (!records) {
      records = new Map();
      progressRecords.set(node, records);
    }
    if (!records.has(key)) records.set(key, { current: null, target: 0 });
    return records.get(key);
  };

  const damp = (record, target, channel = 'section') => {
    const context = frameContext;
    const reduced = context?.reduced ?? reducedQuery.matches;
    const value = clamp(target);
    record.target = value;
    if (record.current === null || reduced) {
      record.current = value;
      return value;
    }
    const config = channelConfig[channel] || channelConfig.section;
    const alpha = 1 - Math.exp(-config.damping * context.deltaTime);
    const desired = (value - record.current) * alpha;
    const maxDelta = config.velocity * context.deltaTime;
    record.current += clamp(desired, -maxDelta, maxDelta);
    if (Math.abs(value - record.current) > .0005) settling = true;
    else record.current = value;
    return clamp(record.current);
  };

  const dampValue = (key, target, channel = 'section') => {
    if (!valueRecords.has(key)) valueRecords.set(key, { current: null, target: 0 });
    return damp(valueRecords.get(key), target, channel);
  };

  const releaseValues = (prefix) => {
    valueRecords.forEach((value, key) => {
      if (String(key).startsWith(prefix)) valueRecords.delete(key);
    });
  };

  const metricFor = (node) => metrics.get(node) || { top: 0, height: 1, bottom: 1 };

  const progress = (node, channel = 'signature') => {
    const metric = metricFor(node);
    const span = Math.max(1, metric.height - frameContext.viewportHeight);
    const target = clamp((frameContext.scrollY - metric.top) / span);
    return damp(recordFor(node, `progress:${channel}`), target, channel);
  };

  const passage = (node, channel = 'section', entry = .88, exit = .16) => {
    const metric = metricFor(node);
    const start = metric.top - frameContext.viewportHeight * entry;
    const span = metric.height + frameContext.viewportHeight * (entry - exit);
    const target = clamp((frameContext.scrollY - start) / Math.max(1, span));
    return damp(recordFor(node, `passage:${channel}:${entry}:${exit}`), target, channel);
  };

  const track = (node) => {
    if (!node) return node;
    tracked.add(node);
    measurePending = true;
    return node;
  };

  const jumpTo = (node, fraction = .5) => {
    const metric = metricFor(node);
    const span = Math.max(1, metric.height - innerHeight);
    window.scrollTo({ top: metric.top + span * clamp(fraction), behavior: 'auto' });
  };

  const api = {
    clamp,
    compact: compactQuery,
    dampValue,
    invalidate() {
      engine.schedule(true);
    },
    invalidateMeasure() {
      measurePending = true;
      engine.schedule(true);
    },
    jumpTo,
    metrics,
    passage,
    progress,
    releaseValues,
    reduced: reducedQuery,
    setThemeOverride(root, phase) {
      if (phase) themeOverrides.set(root, phase);
    },
    smoothstep,
    tablet: tabletQuery,
    track
  };

  const mount = (scope = document) => {
    factories.forEach((factory, selector) => {
      const nodes = [];
      if (scope.matches?.(selector)) nodes.push(scope);
      scope.querySelectorAll?.(selector).forEach((node) => nodes.push(node));
      nodes.forEach((node) => {
        if (instances.has(node)) return;
        track(node);
        const instance = factory(node, api) || {};
        instances.set(node, instance);
      });
    });
    sections = [...document.querySelectorAll('[data-twlh-section]')];
    sections.forEach(track);
    measurePending = true;
    engine.schedule(true);
  };

  const unmount = (scope) => {
    instances.forEach((instance, node) => {
      if (node === scope || scope.contains?.(node)) {
        instance.destroy?.();
        instances.delete(node);
        tracked.delete(node);
      }
    });
    [...tracked].forEach((node) => {
      if (!node?.isConnected || node === scope || scope.contains?.(node)) tracked.delete(node);
    });
    sections = [...document.querySelectorAll('[data-twlh-section]')];
    measurePending = true;
  };

  const updateSectionStates = () => {
    const vh = frameContext.viewportHeight;
    const s = seq();
    // Austritt zuerst und vollstaendig, Eintritt erst nach der Materialuebergabe.
    const enterFrom = compactQuery.matches ? .82 : s.enterFrom;
    const enterTo = compactQuery.matches ? .36 : s.enterTo;
    const exitFrom = s.exitFrom;
    const exitTo = s.exitTo;
    const transFrom = s.settle + zoneFor(true);
    const transTo = s.settle;
    sections.forEach((section) => {
      const metric = metricFor(section);
      const earlyPosition = section.classList.contains('twlh-position') && !compactQuery.matches;
      const sectionEnterFrom = earlyPosition ? .86 : enterFrom;
      const sectionEnterTo = earlyPosition ? .46 : enterTo;
      const enterTarget = smoothstep(metric.top - vh * sectionEnterFrom, metric.top - vh * sectionEnterTo, frameContext.scrollY);
      const exitTarget = smoothstep(metric.bottom - vh * exitFrom, metric.bottom - vh * exitTo, frameContext.scrollY);
      const transitionTarget = smoothstep(metric.bottom - vh * transFrom, metric.bottom - vh * transTo, frameContext.scrollY);
      const enter = damp(recordFor(section, 'section-enter'), enterTarget, 'section');
      const exit = damp(recordFor(section, 'section-exit'), exitTarget, 'text');
      const transition = damp(recordFor(section, 'section-transition'), transitionTarget, 'material');
      setStyle(section, '--tw-section-enter', enter.toFixed(5));
      setStyle(section, '--tw-section-exit', exit.toFixed(5));
      setStyle(section, '--tw-section-transition', transition.toFixed(5));
      const state = exit > .92 ? 'complete' : enter < .08 ? 'entering' : exit > .08 ? 'leaving' : 'active';
      setDataset(section, 'homeState', state);
    });
  };

  const updateSurfaces = () => {
    const targets = Object.fromEntries(surfaceNames.map((name) => [name, 0]));
    let from = 0;
    let to = 0;
    let transition = 0;

    for (let index = 1; index < sections.length; index += 1) {
      const boundary = metricFor(sections[index]).top;
      const fromName = sections[index - 1]?.dataset.homeSurface || 'obsidian';
      const toName = sections[index]?.dataset.homeSurface || fromName;
      // Hell<->Dunkel ist ein echter Materialwechsel und darf etwas laenger sein.
      const lightFrom = ['paper', 'product'].includes(fromName);
      const lightTo = ['paper', 'product'].includes(toName);
      const material = lightFrom !== lightTo;
      // Der Wechsel ist abgeschlossen, BEVOR die neue Section den Lesebereich erreicht.
      const end = boundary - frameContext.viewportHeight * zoneSettle();
      const start = end - frameContext.viewportHeight * zoneFor(material);
      if (frameContext.scrollY < start) break;
      from = index - 1;
      to = index;
      transition = frameContext.scrollY >= end ? 1 : smoothstep(start, end, frameContext.scrollY);
      if (transition === 1) from = to;
    }

    const fromSurface = sections[from]?.dataset.homeSurface || 'obsidian';
    const toSurface = sections[to]?.dataset.homeSurface || fromSurface;
    if (from === to || fromSurface === toSurface) targets[toSurface] = 1;
    else {
      targets[fromSurface] = 1 - transition;
      targets[toSurface] = transition;
    }

    let dominantSurface = fromSurface;
    let dominantValue = -1;
    surfaceNames.forEach((name) => {
      const value = dampValue(`surface:${name}`, targets[name], 'material');
      setStyle(surfaceLayers.get(name), '--tw-surface-opacity', value.toFixed(5));
      if (value > dominantValue) {
        dominantValue = value;
        dominantSurface = name;
      }
    });

    const dominantIndex = transition > .56 ? to : from;
    const currentSection = sections[dominantIndex] || sections[0];
    let phase = ['paper', 'product'].includes(dominantSurface) ? 'light' : 'dark';
    const override = themeOverrides.get(currentSection);
    if (override) phase = override;
    setDataset(body, 'homeSurface', dominantSurface);
    setDataset(body, 'homeCurrent', currentSection?.dataset.homeSection || '01');
    setDataset(body, 'twlhPhase', phase);
    setStyle(body, '--tw-home-transition-progress', dampValue('surface:transition', transition, 'material').toFixed(5));
  };

  const homeDriver = {
    collect(scope = document) {
      if (destroyed) return;
      mount(scope);
    },
    remove(scope) {
      if (destroyed) return;
      unmount(scope);
    },
    update(state) {
      if (destroyed) return false;
      const now = performance.now();
      const elapsed = lastTime ? (now - lastTime) / 1000 : 1 / 60;
      lastTime = now;
      frameContext = {
        deltaTime: clamp(elapsed, 1 / 240, .05),
        reduced: state.reduced,
        scrollY: state.scrollY,
        viewportHeight: state.height,
        viewportWidth: state.width
      };
      settling = false;
      themeOverrides.clear();
      if (measurePending || state.force) measure();
      updateSectionStates();
      instances.forEach((instance, node) => {
        const metric = metricFor(node);
        const margin = frameContext.viewportHeight * 1.75;
        const nearViewport = metric.bottom >= frameContext.scrollY - margin
          && metric.top <= frameContext.scrollY + frameContext.viewportHeight + margin;
        if (nearViewport || frameContext.reduced) instance.update?.(frameContext);
      });
      updateSurfaces();
      return settling;
    }
  };

  document.querySelectorAll('[data-home-surface-layer]').forEach((layer) => surfaceLayers.set(layer.dataset.homeSurfaceLayer, layer));

  window.TWLuxuryHome = {
    version: '2.0.0',
    compact: compactQuery,
    reduced: reducedQuery,
    register(selector, factory) {
      factories.set(selector, factory);
      mount(document);
    },
    invalidate: api.invalidate,
    invalidateMeasure: api.invalidateMeasure
  };

  if (main && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => api.invalidateMeasure());
    resizeObserver.observe(main);
    main.addEventListener('load', api.invalidateMeasure, true);
  }
  document.fonts?.ready.then(api.invalidateMeasure);
  engine.register(homeDriver);
})();
